import {
  BirthdayIcon,
  Button,
  DotsVerticalIcon,
  Dropdown,
  DropdownMenu,
  type DropdownMenuItem,
  Tooltip,
} from '@bp/ui-components';
import classNames from 'classnames';
import { graphql } from 'client/gql';
import type { DashboardEvent } from 'components/Home/getDashboardEvents';
import dayjs from 'dayjs';
import { useConfirm } from 'hooks/useConfirm';
import { forwardRef, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { useWindowDimensions } from 'utils/dimensions';
import { copyEventLinkToClipboard, getTypeText } from 'utils/eventHelper';
import { eventColors } from '../../utils/dateCalculations';
import type { BpEventType } from '../EventsList/EventsList';
import styles from './EventItem.module.scss';

const BpDeleteCalendarEventMutationDocument = graphql(`
  mutation BpDeleteCalendarEvent($where: CalendarEventWhere!) {
    deleteCalendarEvents(where: $where) {
      nodesDeleted
    }
  }
`);

type Option = DashboardEvent | BpEventType;

type EventItemProps<T extends Option> = {
  event: T;
  showClassNames?: boolean;
  isCompact: boolean;
  isEmbedded?: boolean;
  isActive?: boolean;
  onClick?: (bpEvent: T) => void;
  onEdit?: (uuid: string) => void;
  className?: string;
};

type EventItemComponent = <T extends Option>(
  props: EventItemProps<T> & { ref?: Ref<HTMLDivElement> },
) => ReturnType<React.FC<EventItemProps<T>>>;

// forwardRef has some problems with TypeScript Generics, this way casting is necessary at the bottom of the file
const UntypedEventItem = forwardRef<HTMLDivElement, EventItemProps<Option>>(
  ({ event, showClassNames, isCompact, isEmbedded = false, isActive, className, onEdit, onClick }, ref) => {
    const { t } = useTranslation();

    const { ConfirmationDialog, confirm: confirmDelete } = useConfirm({
      defaultTitle: t('delete.headline'),
      defaultMessage: t('delete.message', {
        type: t('appointments.titleSingular'),
        context: 'male',
      }),
      defaultConfirmText: t('delete.delete'),
    });

    const { isPhone } = useWindowDimensions();

    const isBirthday = event.type === 'birthday';
    const isEditable = onEdit !== undefined;

    const duration = dayjs.duration(Number(event.duration), 's');
    const start = dayjs(event?.start).format('HH:mm').toString();
    const end = dayjs(event?.start).add(duration).format('HH:mm');

    const [, deleteEvent] = useMutation(BpDeleteCalendarEventMutationDocument);

    const handleDelete = () => {
      if (event.uuid) deleteEvent({ where: { uuid: event.uuid } });
    };

    const actions: DropdownMenuItem[] = [
      {
        onClick: () => {
          if (onEdit) onEdit(event.uuid);
        },
        label: t('common.edit'),
      },
      {
        label: t('meetings.copyLink'),
        onClick: () => {
          const e = event as BpEventType;
          if (!e || event.virtualLocationsCount === 0) return;
          copyEventLinkToClipboard(e.virtualLocations[0].uri); // TODO: handle multiple virtual locations
        },
        disabled: event.virtualLocationsCount === 0,
      },
      { type: 'ruler' },
      {
        label: t('delete.delete'),
        color: 'error',
        onClick: async () => {
          const res = await confirmDelete();
          if (res) {
            handleDelete();
          }
        },
      },
    ];

    const eventItemClasses = classNames(
      styles['event-item'],
      {
        [styles.compact]: isCompact,
        [styles.embedded]: isEmbedded,
        [styles.active]: isActive,
        [styles.birthday]: isBirthday,
      },
      className,
    );

    const color = eventColors(event.categories ?? [], isBirthday);
    const dayClasses = classNames(styles.day, styles[color]);

    return (
      <>
        <div
          id={event.uuid}
          className={eventItemClasses}
          onClick={() => {
            if (onClick) {
              onClick(event);
            }
          }}
          ref={ref}
        >
          <div className={dayClasses}>
            {showClassNames && event.classShortNames && event.classShortNames.length > 0 ? (
              event.classShortNames.length > 1 ? (
                <Tooltip triggerClass={styles.tooltip} content={event.classShortNames.join(', ')}>
                  <div className={styles['class-name']}>{event.classShortNames[0]}</div>
                  <div className={styles.hint}>+{event.classShortNames.length - 1}</div>
                </Tooltip>
              ) : (
                event.classShortNames[0]
              )
            ) : (
              dayjs(event.start).format('D')
            )}
          </div>
          <div className={styles.content}>
            <div className={styles.top}>
              {isBirthday && <BirthdayIcon className={styles.icon} />}
              <div className={styles.title}>{event.title}</div>
              {!isBirthday && (
                <div className={styles.info}>
                  {start} - {end} | {getTypeText(event.categories, isPhone ? 'short' : 'long')}
                  <div className={styles.time}></div>
                  <div className={styles.type}></div>
                </div>
              )}
            </div>
            {!isBirthday && (
              <>
                {isCompact && event.originDisplayName !== '' && (
                  <div className={styles.origin}>{event.originDisplayName}</div>
                )}
                {!isCompact && (
                  <>
                    {event.description && <div className={styles.description}>{event.description}</div>}
                    {event.keywords && event.keywords.length > 0 && <div className={styles.tag}>{event.keywords}</div>}
                  </>
                )}
              </>
            )}
          </div>
          {!isBirthday && isEditable && (
            <div className={styles.action}>
              <Dropdown noPadding trigger={<Button hierarchy='ghost' icon={<DotsVerticalIcon className='small' />} />}>
                <DropdownMenu data={actions} />
              </Dropdown>
            </div>
          )}
        </div>

        <ConfirmationDialog />
      </>
    );
  },
);
UntypedEventItem.displayName = 'EventItem';

export const EventItem = UntypedEventItem as EventItemComponent;
